import { create } from 'zustand'
import { auth as authApi, type LoginResult } from '../api'
import type { User, Permissions } from '../types'

type AuthStatus = 'loading' | 'setup' | 'login' | 'authenticated'

interface AuthState {
  status: AuthStatus
  user: User | null
  permissions: Permissions | null
  /** ログイン後にパスワード変更を促すフラグ（ログイン成功直後に一度だけtrue） */
  passwordExpired: boolean
  /**
   * MFA未設定 admin の一時セッション（session_id が `setup_*`）にいる状態。
   * true のとき App.tsx は /mfa/setup へ強制遷移し、業務 API の連続 403 を防ぐ。
   */
  isSetupSession: boolean
  checkAuth: () => Promise<void>
  /**
   * ログイン。`mfaRequired` / `mfaSetupRequired` / `passwordExpired` 情報を返す。
   * LoginPage 側で分岐して MFA 入力欄表示やリダイレクトに使う。
   */
  login: (email: string, password: string, totp?: string) => Promise<LoginResult>
  logout: () => Promise<void>
  clearPasswordExpired: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'loading',
  user: null,
  permissions: null,
  passwordExpired: false,
  isSetupSession: false,

  checkAuth: async () => {
    // 2026-05-25 M-11 修正: API 接続エラーを setup 未完了と区別する。
    //   旧版は catch で常に setup 画面に遷移させていたため、本番で D1 が一時的に
    //   応答不能な場合「既にセットアップ済みです」エラーで詰むユーザー体験になっていた。
    //   - 真の「未セットアップ」（HTTP 200 + { needsSetup: true }）→ setup 画面
    //   - HTTP 502/503/timeout 等の接続エラー → login 画面を表示し再試行を促す
    //     （未セットアップなら login で「セットアップ済み？」と聞かれて結局 setup に行くので、
    //      間違って setup に飛ばすよりも login のほうが回復可能で安全）
    try {
      const setupStatus = await authApi.checkSetup()
      if (setupStatus.needsSetup) {
        set({ status: 'setup' })
        return
      }
    } catch (e) {
      console.warn('[checkAuth] checkSetup failed (treating as login):', e)
      set({ status: 'login', isSetupSession: false })
      return
    }
    try {
      const data = await authApi.me()
      set({
        status: 'authenticated',
        user: data.user,
        permissions: data.permissions,
        isSetupSession: !!data.isSetupSession,
      })
    } catch {
      set({ status: 'login', isSetupSession: false })
    }
  },

  login: async (email, password, totp) => {
    const result = await authApi.login(email, password, totp)

    // MFA が必要なフローは認証状態を変えずに結果だけ返す
    if (result.mfaRequired || result.mfaSetupRequired) {
      return result
    }

    // 通常ログイン成功 → /auth/me で認証状態を確定
    const data = await authApi.me()
    set({
      status: 'authenticated',
      user: data.user,
      permissions: data.permissions,
      passwordExpired: !!result.passwordExpired,
      isSetupSession: !!data.isSetupSession,
    })
    return result
  },

  logout: async () => {
    await authApi.logout()
    // SSO 経由フラグもクリア（次回ログイン時にアドバイザーリンクを残さない）
    try {
      sessionStorage.removeItem('advisor_origin')
      sessionStorage.removeItem('advisor_url')
    } catch { /* ignore */ }
    set({ status: 'login', user: null, permissions: null, passwordExpired: false, isSetupSession: false })
  },

  clearPasswordExpired: () => set({ passwordExpired: false }),
}))
